import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import PhoneNumberInput from "../userRegistration/PhoneNumberInput";
import { parsePhoneNumberFromString } from "libphonenumber-js";

export default function EditEntityModal({
  title = "Edit",
  row,
  isBusy = false,
  allowCountryEdit = false,
  onClose,
  onSubmit,
  includeEmail = false,
  emailRequired = false,
  variant = "person", 
}) {
  const isBusiness = variant === "business";

  const [firstName, setFirstName] = useState(row?.firstName || "");
  const [lastName, setLastName] = useState(row?.lastName || "");
  const [name, setName] = useState(row?.name || "");
  const [country, setCountry] = useState(row?.country || "");
  const [email, setEmail] = useState(
    isBusiness ? row?.contactEmail || "" : row?.email || ""
  );
  const [contactName, setContactName] = useState(row?.contactName || "");

  const initialPhoneRaw = useMemo(() => {
    const phoneVal = isBusiness ? row?.contactPhone : row?.phone;
    return String(phoneVal || "").replace(/^\+/, "");
  }, [row, isBusiness]);

  const [phoneRaw, setPhoneRaw] = useState(initialPhoneRaw);
  const [phoneValid, setPhoneValid] = useState(true);

  useEffect(() => setPhoneRaw(initialPhoneRaw), [initialPhoneRaw]);
  useEffect(() => {
    setEmail(isBusiness ? row?.contactEmail || "" : row?.email || "");
  }, [row, isBusiness]);

  const emailLooksValid = (v) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());

  const handleSubmit = (e) => {
    e.preventDefault();

    if (isBusiness) {
      if (!name.trim() || !country.trim()) {
        toast.error("Name and country are required.");
        return;
      }
      if (name.length > 120) {
        toast.error("Name cannot exceed 120 characters.");
        return;
      }
      if (includeEmail) {
        const v = String(email || "").trim();
        if (emailRequired && !v) {
          toast.error("Email is required.");
          return;
        }
        if (v && !emailLooksValid(v)) {
          toast.error("Please enter a valid email address.");
          return;
        }
        if (v && v.length > 190) {
          toast.error("Email is too long.");
          return;
        }
      }
      if (phoneRaw?.trim()) {
        const phoneE164 = phoneRaw.startsWith("+") ? phoneRaw : `+${phoneRaw}`;
        const parsed = parsePhoneNumberFromString(phoneE164);
        if (!parsed || !parsed.isValid()) {
          toast.error("Please enter a valid phone number.");
          return;
        }
        if (phoneE164.length > 20) {
          toast.error("Phone number is too long.");
          return;
        }
        onSubmit({
          name: name.trim(),
          country: country.trim().toUpperCase(),
          contactName: contactName?.trim() || null,
          contactEmail: includeEmail ? String(email || "").trim() || null : null,
          contactPhone: phoneE164,
        });
        return;
      }
      onSubmit({
        name: name.trim(),
        country: country.trim().toUpperCase(),
        contactName: contactName?.trim() || null,
        contactEmail: includeEmail ? String(email || "").trim() || null : null,
        contactPhone: null,
      });
      return;
    }

    if (!firstName.trim() || !lastName.trim() || !country.trim() || !phoneRaw.trim()) {
      toast.error("All fields are required.");
      return;
    }
    if (firstName.length > 50 || lastName.length > 50) {
      toast.error("First/Last name cannot exceed 50 characters.");
      return;
    }
    if (includeEmail) {
      const v = String(email || "").trim();
      if (emailRequired && !v) {
        toast.error("Email is required.");
        return;
      }
      if (v && !emailLooksValid(v)) {
        toast.error("Please enter a valid email address.");
        return;
      }
      if (v.length > 100) {
        toast.error("Email is too long.");
        return;
      }
    }
    const phoneE164 = phoneRaw.startsWith("+") ? phoneRaw : `+${phoneRaw}`;
    const parsed = parsePhoneNumberFromString(phoneE164);
    if (!parsed || !parsed.isValid()) {
      toast.error("Please enter a valid phone number.");
      return;
    }
    if (phoneE164.length > 20) {
      toast.error("Phone number is too long.");
      return;
    }
    const payload = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phone: phoneE164,
      ...(includeEmail ? { email: String(email || "").trim() } : {}),
    };
    onSubmit(payload);
  };

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100"
      style={{ background: "rgba(17,24,39,.45)", zIndex: 1200 }}
      aria-modal="true"
      role="dialog"
      aria-labelledby="edit-entity-modal-title"
    >
      <div className="d-flex w-100 h-100 justify-content-center align-items-center p-3 p-sm-4">
        <section className="ui-panel w-100" style={{ maxWidth: 560 }}>
          <header className="ui-panel__header">
            <div className="d-flex align-items-center justify-content-between">
              <h3 id="edit-entity-modal-title" className="ui-title mb-0">
                {title}
              </h3>
              <div className="ui-actions m-0">
                <button className="ui-btn" onClick={onClose} disabled={isBusy} type="button">
                  Close
                </button>
              </div>
            </div>
            <div className="ui-subtitle mt-1">
              {isBusiness
                ? "Update business profile"
                : `Update basic details${includeEmail ? ", email" : ""} and phone number.`}
            </div>
          </header>

          <form onSubmit={handleSubmit}>
            <div className="p-3 p-md-4">
              <div className="row g-3">
                {isBusiness ? (
                  <>
                    <div className="col-12">
                      <label className="form-label">Business name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        maxLength={120}
                        required
                        disabled={isBusy}
                        autoComplete="organization"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="col-12 col-md-6">
                      <label className="form-label">First name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        maxLength={50}
                        required
                        disabled={isBusy}
                        autoComplete="given-name"
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label">Last name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        maxLength={50}
                        required
                        disabled={isBusy}
                        autoComplete="family-name"
                      />
                    </div>
                  </>
                )}

                {includeEmail && (
                  <div className="col-12">
                    <label className="form-label">Email</label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="bi bi-envelope" />
                      </span>
                      <input
                        type="email"
                        className="form-control"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        disabled={isBusy}
                        required={emailRequired}
                        autoComplete="email"
                      />
                    </div>
                  </div>
                )}

                {isBusiness && (
                  <div className="col-12">
                    <label className="form-label">Contact name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={contactName}
                      onChange={(e) => setContactName(e.target.value)}
                      disabled={isBusy}
                      maxLength={120}
                      autoComplete="name"
                    />
                  </div>
                )}

                <div className="col-12 col-md-6">
                  <label className="form-label">{isBusiness ? "Country (ISO-2)" : "Country"}</label>
                  <input
                    type="text"
                    className="form-control"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    required
                    disabled={isBusy || allowCountryEdit}
                    maxLength={isBusiness ? 2 : undefined}
                    placeholder={isBusiness ? "SE" : ""}
                    autoComplete="country-name"
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label">{isBusiness ? "Contact phone" : "Phone"}</label>
                  <PhoneNumberInput
                    countryName={country || "Sweden"}
                    value={phoneRaw}
                    onChange={({ phone, isValid }) => {
                      setPhoneRaw(String(phone || "").replace(/^\+/, ""));
                      setPhoneValid(Boolean(isValid));
                    }}
                  />
                  {!phoneValid && (
                    <div className="form-text text-danger">
                      Please enter a valid phone number for {country || "the selected country"}.
                    </div>
                  )}
                  {!isBusiness && !phoneRaw?.trim() && (
                    <div className="form-text text-danger">Phone is required.</div>
                  )}
                </div>
              </div>
            </div>

            <div className="ui-actions px-3 px-md-4 pb-3 pb-md-4 w-100 justify-content-end gap-2">
              <button type="button" className="ui-btn" onClick={onClose} disabled={isBusy}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-success rounded-pill"
                disabled={
                  isBusy ||
                  !phoneValid ||
                  (variant === "person" && !phoneRaw?.trim())
                }
              >
                {isBusy ? "Saving…" : "Save changes"}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
}
